import { AxiosError } from 'axios'

import closeAction from './CloseBtnEnq'
import { randomId } from 'common/helpers/strings'
import { isFriendlyError, isNetworkError } from 'common/helpers/isFriendlyError'
import { enqueueNotification, Notification, NotificationLevels } from 'store/notifier'

const OMITTED_ERROR_KEYS = new Set(['additional_details'])
const NO_PREFIX_ERROR_KEYS = new Set(['detail', 'non_field_errors', 'message'])

const formatErrorKey = (key: string) => key.replace(/_/g, ' ')

type ApiErrorBody = { detail?: string; additional_details?: Record<string, string> }

const errorBody = (error: AxiosError): ApiErrorBody | string | undefined =>
  error.response?.data as ApiErrorBody | string | undefined

const collectValidationMessages = (value: unknown, prefix = ''): string[] => {
  if (value == null) return []

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const normalizedValue = String(value).trim()
    if (!normalizedValue) return []
    return [prefix ? `${prefix}: ${normalizedValue}` : normalizedValue]
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectValidationMessages(item, prefix))
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).flatMap(([key, nestedValue]) => {
      if (OMITTED_ERROR_KEYS.has(key)) {
        return []
      }

      const nextPrefix = NO_PREFIX_ERROR_KEYS.has(key)
        ? prefix
        : prefix
        ? `${prefix}.${formatErrorKey(key)}`
        : formatErrorKey(key)

      return collectValidationMessages(nestedValue, nextPrefix)
    })
  }

  return []
}

const getReadableAxiosError = (error: AxiosError): { title?: string; message?: string } => {
  const body = errorBody(error)
  if (typeof body === 'string') {
    return { title: body, message: '' }
  }

  if (body?.detail) {
    return { title: body.detail, message: '' }
  }

  const validationMessages = collectValidationMessages(body)
  if (validationMessages.length === 0) {
    return {}
  }

  return {
    title: validationMessages[0],
    message: validationMessages.slice(1).join('\n')
  }
}

const enqueue = (data: AxiosError | Notification, customTitle?: string) => {
  if ('isAxiosError' in data) {
    const key = randomId()
    let title = customTitle || data.message || data.code
    let message = customTitle ? '' : data.stack || ''
    let dismissed = false
    if (data.response?.status && data.response?.status >= 500 && isFriendlyError(data)) {
      sessionStorage.setItem('serverError', String(data.response?.data))
      dismissed = true
    } else if (isNetworkError(data)) {
      title = 'Network Error'
      message = 'There was a network issue. Please check your internet connection and try again.'
    }
    if (!customTitle) {
      const readableError = getReadableAxiosError(data)
      if (readableError.title) {
        title = readableError.title
        message = readableError.message || ''
      }
    }
    const body = errorBody(data)
    const additionalDetails = typeof body === 'string' ? undefined : body?.additional_details
    if (additionalDetails) {
      Object.keys(additionalDetails).forEach((key) => {
        try {
          console.error(`${key}:\n\n${additionalDetails[key]}`)
        } catch (error) {}
      })
    }

    return enqueueNotification({
      key,
      title,
      message,
      data: {
        ...data,
        request: data.request,
        response: data.response
      },
      options: {
        variant: NotificationLevels.WARNING,
        persist: true,
        action: () => closeAction(key, NotificationLevels.WARNING)
      },
      dismissed
    })
  }
  const { key, title, message, options } = data
  return enqueueNotification({
    key,
    message,
    title,
    data,
    options: {
      ...options,
      ...{
        variant: data?.options?.variant || NotificationLevels.WARNING,
        persist: data?.options?.autoHideDuration ? false : true,
        action: () => closeAction(key, data?.options?.variant || NotificationLevels.WARNING),
        autoHideDuration: data?.options?.autoHideDuration || undefined
      }
    }
  })
}

export { enqueue }
